import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi, useAuthStore } from '@/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';

export default function Login2FA() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const credentials = useAuthStore((s) => s.pendingCredentials);
  const token = useAuthStore((s) => s.token);
  const setToken = useAuthStore((s) => s.setToken);
  const setClinician = useAuthStore((s) => s.setClinician);
  const setPendingCredentials = useAuthStore((s) => s.setPendingCredentials);

  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bounce to /login only if the user landed here directly without going
  // through the credential step (no pending creds AND no token). A
  // successful OTP submit clears pendingCredentials *before* navigate() can
  // apply, so without the `token` guard, this effect was racing the
  // navigate('/patients') call and winning — sending the user back to
  // /login even though auth had succeeded.
  useEffect(() => {
    if (!credentials && !token) navigate('/login', { replace: true });
  }, [credentials, token, navigate]);

  if (!credentials && !token) return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await authApi.authorize({
        username: credentials.username,
        password: credentials.password,
        otp,
      });
      const accessToken = response.oauth_token?.access_token;
      if (!accessToken) {
        setError(t('The code is incorrect or expired'));
        return;
      }
      // Order matters: navigate FIRST, then clear pendingCredentials. The
      // effect above subscribes to credentials and would race a
      // navigate('/patients') if we cleared first — though the token guard
      // also prevents that, doing it in this order is the belt-and-braces fix.
      setToken(accessToken);
      if (response.clinician) setClinician(response.clinician);
      track(ANALYTICS_EVENTS.LOGIN, { mfa: true });
      navigate('/patients', { replace: true });
      setPendingCredentials(null);
    } catch {
      setError(t('The code is incorrect or expired'));
      toast({ title: t('Verification failed'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('Two-factor authentication')}</CardTitle>
          <CardDescription>
            {t('Enter the 6-digit code we sent to your email.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1">
              <Label htmlFor="otp">{t('Verification code')}</Label>
              <Input
                id="otp"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting || otp.length < 6}>
              {submitting ? t('Verifying…') : t('Verify')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setPendingCredentials(null);
                navigate('/login', { replace: true });
              }}
            >
              {t('Back to sign in')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
